import { Paper, Typography, Box } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

interface Props {
  data: Record<string, number | string>[];
  series: { bucket: string; label: string; color: string }[];
}

/**
 * 📈 일별 채널 유입 추이 (스택 막대)
 * - 대시보드 DailyTrendChart와 동일한 셸
 * - 시리즈는 건수 상위 채널 + '기타' 합산 (useInflowStats에서 구성)
 */
const InflowDailyChart = ({ data, series }: Props) => {
  const total = data.reduce((sum, day) => {
    return sum + series.reduce((s, sr) => s + (Number(day[sr.bucket]) || 0), 0);
  }, 0);

  return (
    <Paper elevation={0} sx={{
      p: { xs: 2, md: 3 }, borderRadius: 3,
      border: '1px solid #e5e7eb', height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography fontWeight={700} sx={{ fontSize: '0.95rem', color: '#0f172a' }}>
          일별 채널 유입 추이
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1976d2' }}>
          총 {total}건
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        일별 신규 신청 건수 (채널별 누적)
      </Typography>
      <Box sx={{ width: '100%', flex: 1, minHeight: 260 }}>
        {total === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography color="text.secondary" fontSize="0.85rem">데이터가 없습니다</Typography>
          </Box>
        ) : (
          <ResponsiveContainer>
            <BarChart data={data} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: '#94a3b8' }}
                axisLine={false} tickLine={false}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#94a3b8' }}
                axisLine={false} tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 10, border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.85rem',
                }}
                formatter={(value: number, name: string) => [`${value}건`, name]}
              />
              <Legend
                wrapperStyle={{ fontSize: '0.78rem', paddingTop: 8 }}
                iconType="circle"
                iconSize={8}
              />
              {series.map((s, idx) => (
                <Bar
                  key={s.bucket}
                  dataKey={s.bucket}
                  name={s.label}
                  stackId="inflow"
                  fill={s.color}
                  stroke="#fff"
                  strokeWidth={1}
                  maxBarSize={40}
                  radius={idx === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

export default InflowDailyChart;
